import React, { useState, useEffect, useMemo } from "react";
import {
  collection,
  getDocs,
  doc,
  setDoc,
  addDoc,
  updateDoc,
  writeBatch,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "./AuthProvider";
import { Heading } from "./heading";
import { Button } from "./button";
import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogBody,
  DialogTextarea,
} from "./dialog";
import { showAlert } from "../alertService";
import { DateTime } from "luxon";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  ACCENT,
  MONTHS_SHORT,
  formatMXN,
  Section,
  CustomTooltip,
} from "./cuentas/shared";
import { fmtMXNInput, normalizeMoneyInput } from "../utils/money";
import { parseBasicInfo } from "./VenuesList";
import {
  BanknotesIcon,
  CalendarDaysIcon,
  PencilSquareIcon,
  PlusIcon,
  TagIcon,
  TrashIcon,
  UserGroupIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";

const TZ = "America/Mexico_City";

// Everything this screen writes lives in the top-level admin-only collections
// `venueBilling/{venueId}` and `venueBillingGroups/{groupId}` — never on the
// venue docs themselves.
const EMPTY_FORM = {
  pagado: false,
  fechaPromesa: "",
  vencimiento: "",
  monto: "",
  grupoId: "",
  comisionNombre: "",
  comisionTipo: "porcentaje",
  comisionValor: "",
  parcialidades: [],
  tags: [],
  notas: "",
};

const ESTADOS = {
  pagado: { text: "Pagado", classes: "bg-green-100 text-green-800" },
  por_vencer: { text: "Por vencer", classes: "bg-amber-100 text-amber-800" },
  vencido: { text: "Vencido", classes: "bg-red-100 text-red-800" },
  parcialidades: { text: "Parcialidades", classes: "bg-purple-100 text-purple-800" },
  promesa: { text: "Promesa", classes: "bg-blue-100 text-blue-800" },
  pendiente: { text: "Pendiente", classes: "bg-gray-100 text-gray-800" },
};

const POR_VENCER_DIAS = 30;

const tsToMillis = (ts) => (ts?.seconds ? ts.seconds * 1000 : null);

const getEstado = (b) => {
  if (!b) return "pendiente";
  const now = Date.now();
  const venc = tsToMillis(b.vencimiento);
  if (venc && venc < now) return "vencido";
  const parc = b.parcialidades || [];
  // A payment plan in progress overrides everything but vencido; once every
  // installment is paid the venue counts as pagado.
  if (parc.length && !parc.every((p) => p.pagado)) return "parcialidades";
  if (b.pagado || parc.length) {
    if (venc && venc < now + POR_VENCER_DIAS * 86400000) return "por_vencer";
    return "pagado";
  }
  if (b.fechaPromesa) return "promesa";
  return "pendiente";
};

const tsToDateInput = (ts) => {
  const ms = tsToMillis(ts);
  if (!ms) return "";
  return DateTime.fromMillis(ms, { zone: TZ }).toFormat("yyyy-MM-dd");
};

const fmtDate = (ts) => {
  const ms = tsToMillis(ts);
  if (!ms) return "—";
  return DateTime.fromMillis(ms, { zone: TZ }).toFormat("dd/MM/yyyy");
};

const dateInputToTimestamp = (str) => {
  if (!str) return null;
  const dt = DateTime.fromISO(str, { zone: TZ }).set({ hour: 12 });
  return dt.isValid ? Timestamp.fromMillis(dt.toMillis()) : null;
};

// Quick vencimiento options: relative jumps plus end-of-year targets, computed
// so the buttons stay useful as years roll over.
const quickVencimientos = () => {
  const now = DateTime.now().setZone(TZ);
  const finDe = (year) =>
    DateTime.fromObject({ year, month: 12, day: 31 }, { zone: TZ });
  return [
    { label: "6 meses", dt: now.plus({ months: 6 }) },
    { label: "1 año", dt: now.plus({ years: 1 }) },
    { label: "2 años", dt: now.plus({ years: 2 }) },
    { label: `Fin ${now.year}`, dt: finDe(now.year) },
    { label: `Fin ${now.year + 1}`, dt: finDe(now.year + 1) },
  ];
};

const EstadoPill = ({ estado, billing }) => {
  const cfg = ESTADOS[estado] || ESTADOS.pendiente;
  let suffix = "";
  if (estado === "promesa" && billing?.fechaPromesa) {
    suffix = ` ${fmtDate(billing.fechaPromesa)}`;
  } else if (estado === "parcialidades") {
    const parc = billing?.parcialidades || [];
    suffix = ` ${parc.filter((p) => p.pagado).length}/${parc.length}`;
  }
  return (
    <span
      className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full whitespace-nowrap ${cfg.classes}`}
    >
      {cfg.text}
      {suffix}
    </span>
  );
};

const AdminVenuePagos = () => {
  const { user } = useAuth();
  const [venues, setVenues] = useState([]);
  const [billing, setBilling] = useState({});
  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState("");
  const [estadoFilter, setEstadoFilter] = useState("all");
  const [filterPublic, setFilterPublic] = useState("");
  const [filterAdmin, setFilterAdmin] = useState("");
  const [filterZona, setFilterZona] = useState("");
  const [filterTag, setFilterTag] = useState("");
  const [groupBy, setGroupBy] = useState("");

  // Edit dialog
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [tagInput, setTagInput] = useState("");
  const [updating, setUpdating] = useState(false);

  // Installment-plan quick generator inside the edit dialog.
  const [planForm, setPlanForm] = useState({ num: "", monto: "", inicio: "" });

  // Groups dialog. tipo "descuento" = % off each venue's monto; tipo "fijo" =
  // one flat price for the whole group (e.g. 5 venues por $200k).
  const [isGruposOpen, setIsGruposOpen] = useState(false);
  const [grupoForm, setGrupoForm] = useState({
    nombre: "",
    tipo: "descuento",
    descuento: "",
    montoFijo: "",
  });
  const [editingGrupoId, setEditingGrupoId] = useState(null);
  const [savingGrupo, setSavingGrupo] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [venuesSnap, billingSnap, gruposSnap] = await Promise.all([
          getDocs(collection(db, "venues")),
          getDocs(collection(db, "venueBilling")),
          getDocs(collection(db, "venueBillingGroups")),
        ]);
        setVenues(
          venuesSnap.docs.map((d) => {
            const data = d.data();
            return {
              id: d.id,
              name: data.name || "(sin nombre)",
              zone: parseBasicInfo(data.basicInfo).zone,
              isActive: data.isActive || false,
              isActiveAdmin: data.isActiveAdmin || false,
            };
          })
        );
        const billingMap = {};
        billingSnap.docs.forEach((d) => {
          billingMap[d.id] = d.data();
        });
        setBilling(billingMap);
        setGrupos(
          gruposSnap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .sort((a, b) => (a.nombre || "").localeCompare(b.nombre || "", "es"))
        );
      } catch (err) {
        console.error("Error fetching venue billing:", err);
        setError("Error al cargar los datos de pagos");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const gruposById = useMemo(() => {
    const map = {};
    grupos.forEach((g) => {
      map[g.id] = g;
    });
    return map;
  }, [grupos]);

  // Names already used in any venue's comision, for the datalist so the same
  // person is always spelled the same way (grouping relies on the name).
  const comisionNombres = useMemo(() => {
    const set = new Set();
    Object.values(billing).forEach((b) => {
      if (b?.comision?.nombre) set.add(b.comision.nombre);
    });
    return [...set].sort((a, b) => a.localeCompare(b, "es"));
  }, [billing]);

  const grupoMemberCounts = useMemo(() => {
    const counts = {};
    Object.values(billing).forEach((b) => {
      if (b?.grupoId) counts[b.grupoId] = (counts[b.grupoId] || 0) + 1;
    });
    return counts;
  }, [billing]);

  // Venues enriched with their billing record, derived estado and the monto
  // neto. For % groups, neto = monto - descuento; for fixed-price groups the
  // group total is split evenly across its members so sums and the chart
  // always add up to the group price exactly once.
  const rows = useMemo(
    () =>
      venues
        .map((v) => {
          const b = billing[v.id] || null;
          const grupo = b?.grupoId ? gruposById[b.grupoId] : null;
          const bruto = Number(b?.monto) || 0;
          let neto;
          if (grupo?.tipo === "fijo") {
            neto =
              (Number(grupo.montoFijo) || 0) /
              (grupoMemberCounts[grupo.id] || 1);
          } else {
            neto = bruto * (1 - (Number(grupo?.descuento) || 0) / 100);
          }
          const comisionCfg = b?.comision || null;
          const comision = comisionCfg
            ? comisionCfg.tipo === "fijo"
              ? Number(comisionCfg.valor) || 0
              : (neto * (Number(comisionCfg.valor) || 0)) / 100
            : 0;
          return {
            ...v,
            billing: b,
            estado: getEstado(b),
            grupo,
            comisionCfg,
            comision,
            bruto,
            neto,
          };
        })
        .sort((a, b) => a.name.localeCompare(b.name, "es")),
    [venues, billing, gruposById, grupoMemberCounts]
  );

  const allTags = useMemo(() => {
    const set = new Set();
    Object.values(billing).forEach((b) =>
      (b?.tags || []).forEach((t) => set.add(t))
    );
    return [...set].sort((a, b) => a.localeCompare(b, "es"));
  }, [billing]);

  const zonaOptions = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => r.zone && set.add(r.zone));
    return [...set].sort((a, b) => a.localeCompare(b, "es"));
  }, [rows]);

  const filteredRows = useMemo(
    () =>
      rows.filter((r) => {
        if (search) {
          const hay = [r.name, r.zone, r.grupo?.nombre, ...(r.billing?.tags || [])]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          if (!hay.includes(search.toLowerCase())) return false;
        }
        if (estadoFilter !== "all" && r.estado !== estadoFilter) return false;
        if (filterPublic === "activo" && !r.isActive) return false;
        if (filterPublic === "inactivo" && r.isActive) return false;
        if (filterAdmin === "activo" && !r.isActiveAdmin) return false;
        if (filterAdmin === "inactivo" && r.isActiveAdmin) return false;
        if (filterZona && r.zone !== filterZona) return false;
        if (filterTag && !(r.billing?.tags || []).includes(filterTag)) return false;
        return true;
      }),
    [rows, search, estadoFilter, filterPublic, filterAdmin, filterZona, filterTag]
  );

  // Grouped view: [{ key, label, rows, subtotal }]. With groupBy="tag" a venue
  // appears under every tag it carries.
  const sections = useMemo(() => {
    if (!groupBy) {
      return [{ key: "_all", label: null, rows: filteredRows }];
    }
    const buckets = new Map();
    const push = (key, label, row) => {
      if (!buckets.has(key)) buckets.set(key, { key, label, rows: [] });
      buckets.get(key).rows.push(row);
    };
    filteredRows.forEach((r) => {
      if (groupBy === "grupo") {
        push(r.grupo?.id || "_none", r.grupo?.nombre || "Sin grupo", r);
      } else if (groupBy === "comisionista") {
        const nombre = r.comisionCfg?.nombre;
        push(
          nombre ? `com:${nombre.toLowerCase()}` : "_none",
          nombre || "Sin comisión",
          r
        );
      } else if (groupBy === "zona") {
        push(r.zone || "_none", r.zone || "Sin ciudad / zona", r);
      } else if (groupBy === "tag") {
        const tags = r.billing?.tags || [];
        if (!tags.length) push("_none", "Sin tags", r);
        else tags.forEach((t) => push(`tag:${t}`, t, r));
      } else if (groupBy === "estado") {
        push(r.estado, ESTADOS[r.estado].text, r);
      }
    });
    return [...buckets.values()]
      .sort((a, b) => {
        if (a.key === "_none") return 1;
        if (b.key === "_none") return -1;
        return a.label.localeCompare(b.label, "es");
      })
      .map((s) => ({
        ...s,
        subtotal: s.rows.reduce((sum, r) => sum + r.neto, 0),
      }));
  }, [filteredRows, groupBy]);

  // Tiles and chart reflect the active filters, so e.g. filtering to venues
  // activos shows the expected revenue for just those.
  const stats = useMemo(() => {
    const counts = {
      pagado: 0,
      por_vencer: 0,
      vencido: 0,
      parcialidades: 0,
      promesa: 0,
      pendiente: 0,
    };
    let ingresoAnual = 0;
    let comisiones = 0;
    filteredRows.forEach((r) => {
      counts[r.estado] += 1;
      ingresoAnual += r.neto;
      comisiones += r.comision;
    });
    return {
      counts,
      ingresoAnual,
      mensual: ingresoAnual / 12,
      comisiones,
      netoTrasComisiones: ingresoAnual - comisiones,
    };
  }, [filteredRows]);

  // Expected cash-in per month for the next 12 months: renewals (vencimiento
  // falls in that month) plus promised payments from venues that haven't paid.
  const chartData = useMemo(() => {
    const start = DateTime.now().setZone(TZ).startOf("month");
    const months = Array.from({ length: 12 }, (_, i) => {
      const dt = start.plus({ months: i });
      return {
        key: dt.toFormat("yyyy-MM"),
        label: `${MONTHS_SHORT[dt.month - 1]} ${String(dt.year).slice(2)}`,
        renovaciones: 0,
        promesas: 0,
        parcialidades: 0,
      };
    });
    const byKey = new Map(months.map((m) => [m.key, m]));
    const monthOf = (ms) =>
      byKey.get(DateTime.fromMillis(ms, { zone: TZ }).toFormat("yyyy-MM"));
    filteredRows.forEach((r) => {
      const parc = r.billing?.parcialidades || [];
      // Unpaid installments are literal agreed amounts; a venue with a payment
      // plan feeds the chart through it instead of through its promesa.
      parc.forEach((p) => {
        const ms = tsToMillis(p.fecha);
        if (p.pagado || !ms) return;
        const m = monthOf(ms);
        if (m) m.parcialidades += Number(p.monto) || 0;
      });
      if (!r.neto) return;
      const venc = tsToMillis(r.billing?.vencimiento);
      if (venc) {
        const m = monthOf(venc);
        if (m) m.renovaciones += r.neto;
      }
      const promesa = tsToMillis(r.billing?.fechaPromesa);
      if (promesa && !r.billing?.pagado && !parc.length) {
        const m = monthOf(promesa);
        if (m) m.promesas += r.neto;
      }
    });
    return months;
  }, [filteredRows]);

  const handleRowClick = (row) => {
    setSelectedVenue(row);
    const b = row.billing;
    setEditForm({
      pagado: b?.pagado || false,
      fechaPromesa: tsToDateInput(b?.fechaPromesa),
      vencimiento: tsToDateInput(b?.vencimiento),
      monto: b?.monto != null ? String(b.monto) : "",
      grupoId: b?.grupoId || "",
      comisionNombre: b?.comision?.nombre || "",
      comisionTipo: b?.comision?.tipo === "fijo" ? "fijo" : "porcentaje",
      comisionValor: b?.comision?.valor != null ? String(b.comision.valor) : "",
      parcialidades: (b?.parcialidades || []).map((p) => ({
        fecha: tsToDateInput(p.fecha),
        monto: p.monto != null ? String(p.monto) : "",
        pagado: !!p.pagado,
      })),
      tags: b?.tags || [],
      notas: b?.notas || "",
    });
    setTagInput("");
    setPlanForm({ num: "", monto: "", inicio: "" });
  };

  const setParcialidad = (index, patch) => {
    setEditForm((prev) => ({
      ...prev,
      parcialidades: prev.parcialidades.map((p, i) =>
        i === index ? { ...p, ...patch } : p
      ),
    }));
  };

  const generarPlan = () => {
    const num = parseInt(planForm.num, 10);
    const monto = normalizeMoneyInput(planForm.monto);
    const inicio = DateTime.fromISO(planForm.inicio, { zone: TZ });
    if (!num || num < 1 || num > 60) {
      showAlert("Indica cuántos pagos son", "error");
      return;
    }
    if (!monto || !(Number(monto) > 0)) {
      showAlert("Indica el monto de cada pago", "error");
      return;
    }
    if (!inicio.isValid) {
      showAlert("Indica la fecha del primer pago", "error");
      return;
    }
    const nuevas = Array.from({ length: num }, (_, i) => ({
      fecha: inicio.plus({ months: i }).toFormat("yyyy-MM-dd"),
      monto,
      pagado: false,
    }));
    setEditForm((prev) => ({
      ...prev,
      parcialidades: [...prev.parcialidades, ...nuevas],
    }));
    setPlanForm({ num: "", monto: "", inicio: "" });
  };

  const addTag = (raw) => {
    const tag = raw.trim();
    if (!tag) return;
    setEditForm((prev) =>
      prev.tags.includes(tag) ? prev : { ...prev, tags: [...prev.tags, tag] }
    );
    setTagInput("");
  };

  const handleSave = async () => {
    if (!selectedVenue) return;
    const parcialidades = editForm.parcialidades.map((p) => ({
      fecha: dateInputToTimestamp(p.fecha),
      monto: Number(normalizeMoneyInput(p.monto)) || 0,
      pagado: !!p.pagado,
    }));
    if (parcialidades.some((p) => !p.fecha || !(p.monto > 0))) {
      showAlert(
        "Completa fecha y monto en todas las parcialidades, o elimina las filas incompletas",
        "error"
      );
      return;
    }
    const comisionNombre = editForm.comisionNombre.trim();
    const comisionValor =
      Number(normalizeMoneyInput(editForm.comisionValor)) || 0;
    if (
      comisionNombre &&
      comisionValor > 0 &&
      editForm.comisionTipo === "porcentaje" &&
      comisionValor > 100
    ) {
      showAlert("La comisión porcentual debe ser de 100% o menos", "error");
      return;
    }
    setUpdating(true);
    try {
      const monto = Number(normalizeMoneyInput(editForm.monto)) || 0;
      const data = {
        pagado: editForm.pagado,
        fechaPromesa: dateInputToTimestamp(editForm.fechaPromesa),
        vencimiento: dateInputToTimestamp(editForm.vencimiento),
        monto,
        grupoId: editForm.grupoId || null,
        comision:
          comisionNombre && comisionValor > 0
            ? {
                nombre: comisionNombre,
                tipo: editForm.comisionTipo,
                valor: comisionValor,
              }
            : null,
        parcialidades,
        tags: editForm.tags,
        notas: editForm.notas || "",
        lastUpdate: Timestamp.now(),
        updatedBy: user?.uid || null,
      };
      await setDoc(doc(db, "venueBilling", selectedVenue.id), data, {
        merge: true,
      });
      setBilling((prev) => ({
        ...prev,
        [selectedVenue.id]: { ...prev[selectedVenue.id], ...data },
      }));
      setSelectedVenue(null);
      showAlert("Registro de pago actualizado", "success");
    } catch (err) {
      console.error("Error saving venue billing:", err);
      showAlert("Error al guardar el registro", "error");
    } finally {
      setUpdating(false);
    }
  };

  // --- Groups CRUD ---

  const resetGrupoForm = () => {
    setGrupoForm({ nombre: "", tipo: "descuento", descuento: "", montoFijo: "" });
    setEditingGrupoId(null);
  };

  const handleSaveGrupo = async () => {
    const nombre = grupoForm.nombre.trim();
    const tipo = grupoForm.tipo;
    const descuento = Number(grupoForm.descuento) || 0;
    if (!nombre) {
      showAlert("El grupo necesita un nombre", "error");
      return;
    }
    if (tipo === "descuento" && (descuento < 0 || descuento > 100)) {
      showAlert("El descuento debe estar entre 0 y 100", "error");
      return;
    }
    let montoFijo = null;
    if (tipo === "fijo") {
      const raw = normalizeMoneyInput(grupoForm.montoFijo);
      if (raw === "" || isNaN(Number(raw)) || Number(raw) <= 0) {
        showAlert("Indica el monto total del grupo", "error");
        return;
      }
      montoFijo = Number(raw);
    }
    setSavingGrupo(true);
    try {
      const data = {
        nombre,
        tipo,
        descuento: tipo === "descuento" ? descuento : 0,
        montoFijo,
        lastUpdate: Timestamp.now(),
      };
      if (editingGrupoId) {
        await updateDoc(doc(db, "venueBillingGroups", editingGrupoId), data);
        setGrupos((prev) =>
          prev.map((g) => (g.id === editingGrupoId ? { ...g, ...data } : g))
        );
      } else {
        const ref = await addDoc(collection(db, "venueBillingGroups"), {
          ...data,
          createdAt: Timestamp.now(),
        });
        setGrupos((prev) =>
          [...prev, { id: ref.id, ...data }].sort((a, b) =>
            a.nombre.localeCompare(b.nombre, "es")
          )
        );
      }
      resetGrupoForm();
      showAlert("Grupo guardado", "success");
    } catch (err) {
      console.error("Error saving group:", err);
      showAlert("Error al guardar el grupo", "error");
    } finally {
      setSavingGrupo(false);
    }
  };

  const handleDeleteGrupo = async (grupo) => {
    const members = Object.entries(billing).filter(
      ([, b]) => b?.grupoId === grupo.id
    );
    if (
      !window.confirm(
        `¿Eliminar el grupo "${grupo.nombre}"?` +
          (members.length
            ? ` Se quitará de ${members.length} venue(s); volverán a su monto individual.`
            : "")
      )
    ) {
      return;
    }
    try {
      const batch = writeBatch(db);
      members.forEach(([venueId]) => {
        batch.update(doc(db, "venueBilling", venueId), { grupoId: null });
      });
      batch.delete(doc(db, "venueBillingGroups", grupo.id));
      await batch.commit();
      setGrupos((prev) => prev.filter((g) => g.id !== grupo.id));
      setBilling((prev) => {
        const next = { ...prev };
        members.forEach(([venueId]) => {
          next[venueId] = { ...next[venueId], grupoId: null };
        });
        return next;
      });
      if (editingGrupoId === grupo.id) resetGrupoForm();
      showAlert("Grupo eliminado", "success");
    } catch (err) {
      console.error("Error deleting group:", err);
      showAlert("Error al eliminar el grupo", "error");
    }
  };

  if (loading) return <div className="p-4">Cargando...</div>;
  if (error) return <div className="p-4 text-red-600">{error}</div>;

  const inputClasses =
    "w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500";

  // Live money preview for the edit dialog, recomputed from the form as the
  // admin types. Member count for fixed groups is the current one, so the
  // per-venue share is an approximation until saved.
  const montoEdit = Number(normalizeMoneyInput(editForm.monto)) || 0;
  const grupoEdit = editForm.grupoId ? gruposById[editForm.grupoId] : null;
  const netoEdit =
    grupoEdit?.tipo === "fijo"
      ? (Number(grupoEdit.montoFijo) || 0) /
        (grupoMemberCounts[grupoEdit.id] || 1)
      : montoEdit * (1 - (Number(grupoEdit?.descuento) || 0) / 100);
  const comisionValorEdit =
    Number(normalizeMoneyInput(editForm.comisionValor)) || 0;
  const comisionEdit =
    editForm.comisionNombre.trim() && comisionValorEdit > 0
      ? editForm.comisionTipo === "fijo"
        ? comisionValorEdit
        : (netoEdit * comisionValorEdit) / 100
      : 0;
  const sumaParcialidades = editForm.parcialidades.reduce(
    (s, p) => s + (Number(normalizeMoneyInput(p.monto)) || 0),
    0
  );
  const cobradoParcialidades = editForm.parcialidades.reduce(
    (s, p) => s + (p.pagado ? Number(normalizeMoneyInput(p.monto)) || 0 : 0),
    0
  );

  return (
    <div className="container mx-auto p-4 space-y-6 max-lg:px-0">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Heading level={2}>Pagos de Venues</Heading>
        <Button outline onClick={() => setIsGruposOpen(true)}>
          <UserGroupIcon className="h-4 w-4" />
          Grupos y descuentos
        </Button>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {[
          { label: "Pagados", value: stats.counts.pagado, classes: "text-green-700" },
          { label: "Por vencer (30d)", value: stats.counts.por_vencer, classes: "text-amber-600" },
          { label: "Vencidos", value: stats.counts.vencido, classes: "text-red-600" },
          { label: "En parcialidades", value: stats.counts.parcialidades, classes: "text-purple-600" },
          { label: "Promesas", value: stats.counts.promesa, classes: "text-blue-600" },
          { label: "Ingreso esperado / año", value: formatMXN(stats.ingresoAnual), classes: "text-gray-900" },
          { label: "Comisiones / año", value: formatMXN(stats.comisiones), classes: "text-red-600" },
          { label: "Neto tras comisiones", value: formatMXN(stats.netoTrasComisiones), classes: "text-gray-900" },
        ].map((tile) => (
          <div
            key={tile.label}
            className="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm"
          >
            <div className="text-xs font-medium uppercase tracking-wide text-zinc-500">
              {tile.label}
            </div>
            <div className={`mt-1 text-xl font-semibold ${tile.classes}`}>
              {tile.value}
            </div>
          </div>
        ))}
      </div>

      {/* Expected revenue chart */}
      <Section icon={BanknotesIcon} title="Ingreso esperado por mes">
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: "#71717a" }}
                axisLine={{ stroke: "#e4e4e7" }}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "#71717a" }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => formatMXN(v)}
                width={80}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="renovaciones" name="Renovaciones" fill={ACCENT} radius={[4, 4, 0, 0]} />
              <Bar dataKey="promesas" name="Promesas de pago" fill="#3B82F6" radius={[4, 4, 0, 0]} />
              <Bar dataKey="parcialidades" name="Parcialidades" fill="#F59E0B" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <p className="mt-2 text-xs text-zinc-500">
          Renovaciones según vencimiento, promesas de pago pendientes y
          parcialidades por cobrar, con descuento de grupo aplicado.
        </p>
      </Section>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
          placeholder="Buscar venue, tag, grupo..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
          value={estadoFilter}
          onChange={(e) => setEstadoFilter(e.target.value)}
        >
          <option value="all">Todos los estados</option>
          {Object.entries(ESTADOS).map(([key, cfg]) => (
            <option key={key} value={key}>{cfg.text}</option>
          ))}
        </select>
        <select
          className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
          value={filterPublic}
          onChange={(e) => setFilterPublic(e.target.value)}
        >
          <option value="">Público: todos</option>
          <option value="activo">Público: activos</option>
          <option value="inactivo">Público: inactivos</option>
        </select>
        <select
          className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
          value={filterAdmin}
          onChange={(e) => setFilterAdmin(e.target.value)}
        >
          <option value="">Admin: todos</option>
          <option value="activo">Admin: activos</option>
          <option value="inactivo">Admin: inactivos</option>
        </select>
        <select
          className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
          value={filterZona}
          onChange={(e) => setFilterZona(e.target.value)}
        >
          <option value="">Todas las zonas</option>
          {zonaOptions.map((z) => (
            <option key={z} value={z}>{z}</option>
          ))}
        </select>
        <select
          className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
          value={filterTag}
          onChange={(e) => setFilterTag(e.target.value)}
        >
          <option value="">Todos los tags</option>
          {allTags.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <select
          className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
          value={groupBy}
          onChange={(e) => setGroupBy(e.target.value)}
        >
          <option value="">Sin agrupar</option>
          <option value="grupo">Agrupar por grupo</option>
          <option value="comisionista">Agrupar por comisionista</option>
          <option value="zona">Agrupar por ciudad / zona</option>
          <option value="tag">Agrupar por tag</option>
          <option value="estado">Agrupar por estado</option>
        </select>
        <div className="ml-auto text-sm text-gray-500">
          {filteredRows.length} de {rows.length} venues
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto ring-1 ring-gray-200 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {["Venue", "Estado", "Promesa", "Vencimiento", "Monto", "Grupo", "Comisión", "Tags"].map((h) => (
                <th
                  key={h}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sections.map((section) => (
              <React.Fragment key={section.key}>
                {section.label && (
                  <tr className="bg-gray-50/70">
                    <td colSpan={8} className="px-6 py-2">
                      <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-gray-600">
                        <span>
                          {section.label}
                          <span className="ml-2 font-normal normal-case text-gray-400">
                            {section.rows.length} venue(s)
                          </span>
                        </span>
                        <span>{formatMXN(section.subtotal)}</span>
                      </div>
                    </td>
                  </tr>
                )}
                {section.rows.map((row) => (
                  <tr
                    key={`${section.key}:${row.id}`}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleRowClick(row)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{row.name}</div>
                      {row.zone && (
                        <div className="text-xs text-gray-500">{row.zone}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <EstadoPill estado={row.estado} billing={row.billing} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {fmtDate(row.billing?.fechaPromesa)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {fmtDate(row.billing?.vencimiento)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {row.grupo?.tipo === "fijo" ? (
                        <div>
                          <span className="font-medium text-gray-900">
                            {formatMXN(row.neto)}
                          </span>
                          <span className="ml-1 text-xs text-gray-400">
                            del grupo
                          </span>
                        </div>
                      ) : row.bruto ? (
                        <div>
                          <span className="font-medium text-gray-900">
                            {formatMXN(row.neto)}
                          </span>
                          {row.grupo?.descuento > 0 && (
                            <span className="ml-1 text-xs text-gray-400 line-through">
                              {formatMXN(row.bruto)}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                      {(row.billing?.parcialidades || []).length > 0 && (
                        <div className="text-xs text-purple-600">
                          {row.billing.parcialidades.filter((p) => p.pagado).length}
                          /{row.billing.parcialidades.length} parcialidades ·{" "}
                          {formatMXN(
                            row.billing.parcialidades.reduce(
                              (s, p) => s + (p.pagado ? Number(p.monto) || 0 : 0),
                              0
                            )
                          )}{" "}
                          cobrado
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {row.grupo ? (
                        <>
                          {row.grupo.nombre}
                          {row.grupo.tipo === "fijo" ? (
                            <span className="ml-1 text-xs text-green-700">
                              {formatMXN(row.grupo.montoFijo)} grupo
                            </span>
                          ) : (
                            row.grupo.descuento > 0 && (
                              <span className="ml-1 text-xs text-green-700">
                                -{row.grupo.descuento}%
                              </span>
                            )
                          )}
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {row.comisionCfg ? (
                        <div>
                          <div className="text-gray-900">
                            {row.comisionCfg.nombre}
                          </div>
                          <div className="text-xs text-red-600">
                            -{formatMXN(row.comision)}
                            {row.comisionCfg.tipo === "porcentaje" && (
                              <span className="text-gray-400">
                                {" "}
                                ({row.comisionCfg.valor}%)
                              </span>
                            )}
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1 max-w-[14rem]">
                        {(row.billing?.tags || []).map((tag) => (
                          <span
                            key={tag}
                            className="inline-flex items-center rounded-full bg-zinc-100 px-2 py-0.5 text-xs text-zinc-700"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
            {!filteredRows.length && (
              <tr>
                <td colSpan={8} className="px-6 py-10 text-center text-sm text-gray-500">
                  Sin resultados con los filtros actuales.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>


      {/* Edit dialog */}
      <Dialog
        size="3xl"
        open={!!selectedVenue}
        onClose={() => setSelectedVenue(null)}
      >
        <DialogTitle>{selectedVenue?.name}</DialogTitle>
        {selectedVenue && (
          <div className="mt-1.5 flex flex-wrap items-center gap-2 text-sm text-zinc-500">
            <EstadoPill
              estado={selectedVenue.estado}
              billing={selectedVenue.billing}
            />
            {selectedVenue.zone && <span>{selectedVenue.zone}</span>}
          </div>
        )}
        <DialogBody>
          <div className="grid gap-4 sm:grid-cols-2">
            {/* Pago */}
            <Section icon={BanknotesIcon} title="Pago">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Estado
                    </label>
                    <div className="grid grid-cols-2 overflow-hidden rounded-lg border border-zinc-300 text-sm font-medium">
                      <button
                        type="button"
                        className={
                          !editForm.pagado
                            ? "bg-zinc-700 py-2 text-white"
                            : "bg-white py-2 text-zinc-500 hover:bg-zinc-50"
                        }
                        onClick={() =>
                          setEditForm((prev) => ({ ...prev, pagado: false }))
                        }
                      >
                        Pendiente
                      </button>
                      <button
                        type="button"
                        className={
                          editForm.pagado
                            ? "py-2 text-white"
                            : "bg-white py-2 text-zinc-500 hover:bg-zinc-50"
                        }
                        style={
                          editForm.pagado ? { backgroundColor: ACCENT } : undefined
                        }
                        onClick={() =>
                          setEditForm((prev) => ({ ...prev, pagado: true }))
                        }
                      >
                        Pagado
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Monto (MXN)
                    </label>
                    <div className="relative">
                      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-zinc-400">
                        $
                      </span>
                      <input
                        type="text"
                        inputMode="decimal"
                        className={`${inputClasses} pl-7`}
                        value={fmtMXNInput(editForm.monto)}
                        onChange={(e) =>
                          setEditForm((prev) => ({
                            ...prev,
                            monto: normalizeMoneyInput(e.target.value),
                          }))
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Promesa de pago
                    </label>
                    <div className="flex items-center gap-1.5">
                      <input
                        type="date"
                        className={inputClasses}
                        value={editForm.fechaPromesa}
                        onChange={(e) =>
                          setEditForm((prev) => ({
                            ...prev,
                            fechaPromesa: e.target.value,
                          }))
                        }
                      />
                      {editForm.fechaPromesa && (
                        <button
                          type="button"
                          className="shrink-0 text-zinc-400 hover:text-zinc-600"
                          title="Quitar promesa"
                          onClick={() =>
                            setEditForm((prev) => ({
                              ...prev,
                              fechaPromesa: "",
                            }))
                          }
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Vencimiento de vigencia
                    </label>
                    <div className="flex items-center gap-1.5">
                      <input
                        type="date"
                        className={inputClasses}
                        value={editForm.vencimiento}
                        onChange={(e) =>
                          setEditForm((prev) => ({
                            ...prev,
                            vencimiento: e.target.value,
                          }))
                        }
                      />
                      {editForm.vencimiento && (
                        <button
                          type="button"
                          className="shrink-0 text-zinc-400 hover:text-zinc-600"
                          title="Quitar vencimiento"
                          onClick={() =>
                            setEditForm((prev) => ({
                              ...prev,
                              vencimiento: "",
                            }))
                          }
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-1.5">
                  {quickVencimientos().map((q) => (
                    <button
                      key={q.label}
                      type="button"
                      className="inline-flex items-center gap-1 rounded-full border border-zinc-300 px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-100"
                      onClick={() =>
                        setEditForm((prev) => ({
                          ...prev,
                          vencimiento: q.dt.toFormat("yyyy-MM-dd"),
                        }))
                      }
                    >
                      <CalendarDaysIcon className="h-3.5 w-3.5" />
                      {q.label}
                    </button>
                  ))}
                </div>
              </div>
            </Section>

            {/* Grupo y comisión */}
            <Section icon={UserGroupIcon} title="Grupo y comisión">
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Grupo (descuento por dueño)
                  </label>
                  <select
                    className={inputClasses}
                    value={editForm.grupoId}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        grupoId: e.target.value,
                      }))
                    }
                  >
                    <option value="">Sin grupo</option>
                    {grupos.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.nombre}
                        {g.tipo === "fijo"
                          ? ` (${formatMXN(g.montoFijo)} grupo)`
                          : g.descuento
                          ? ` (-${g.descuento}%)`
                          : ""}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Comisión (vacío = sin comisión)
                  </label>
                  <input
                    type="text"
                    className={inputClasses}
                    list="admin-billing-comisionistas"
                    placeholder="Nombre de la persona"
                    value={editForm.comisionNombre}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        comisionNombre: e.target.value,
                      }))
                    }
                  />
                  <datalist id="admin-billing-comisionistas">
                    {comisionNombres.map((n) => (
                      <option key={n} value={n} />
                    ))}
                  </datalist>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <select
                      className={inputClasses}
                      value={editForm.comisionTipo}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          comisionTipo: e.target.value,
                        }))
                      }
                    >
                      <option value="porcentaje">% del monto</option>
                      <option value="fijo">Monto fijo</option>
                    </select>
                    {editForm.comisionTipo === "porcentaje" ? (
                      <div className="relative">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          className={`${inputClasses} pr-8`}
                          value={editForm.comisionValor}
                          onChange={(e) =>
                            setEditForm((prev) => ({
                              ...prev,
                              comisionValor: e.target.value,
                            }))
                          }
                        />
                        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-zinc-400">
                          %
                        </span>
                      </div>
                    ) : (
                      <div className="relative">
                        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-zinc-400">
                          $
                        </span>
                        <input
                          type="text"
                          inputMode="decimal"
                          className={`${inputClasses} pl-7`}
                          value={fmtMXNInput(editForm.comisionValor)}
                          onChange={(e) =>
                            setEditForm((prev) => ({
                              ...prev,
                              comisionValor: normalizeMoneyInput(e.target.value),
                            }))
                          }
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="rounded-lg bg-zinc-50 px-3 py-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-500">
                      {grupoEdit?.tipo === "fijo"
                        ? "Parte del precio de grupo"
                        : grupoEdit?.descuento > 0
                        ? `Neto con descuento (-${grupoEdit.descuento}%)`
                        : "Neto"}
                    </span>
                    <span className="font-medium text-zinc-900">
                      {formatMXN(netoEdit)}
                    </span>
                  </div>
                  {comisionEdit > 0 && (
                    <>
                      <div className="flex items-center justify-between">
                        <span className="text-zinc-500">
                          Comisión {editForm.comisionNombre.trim()}
                        </span>
                        <span className="font-medium text-red-600">
                          -{formatMXN(comisionEdit)}
                        </span>
                      </div>
                      <div className="mt-1 flex items-center justify-between border-t border-zinc-200 pt-1">
                        <span className="text-zinc-500">Neto final</span>
                        <span className="font-semibold text-zinc-900">
                          {formatMXN(netoEdit - comisionEdit)}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </Section>

            {/* Parcialidades */}
            <Section
              icon={CalendarDaysIcon}
              title="Parcialidades"
              className="sm:col-span-2"
              action={
                editForm.parcialidades.length > 0 ? (
                  <span className="text-xs font-normal normal-case text-zinc-500">
                    Suma {formatMXN(sumaParcialidades)} · Cobrado{" "}
                    {formatMXN(cobradoParcialidades)}
                  </span>
                ) : null
              }
            >
              {editForm.parcialidades.length > 0 ? (
                <div className="space-y-1.5">
                  <div className="grid grid-cols-[1fr_1fr_4.5rem_1.5rem] gap-2 px-1 text-[11px] font-medium uppercase tracking-wide text-zinc-400">
                    <span>Fecha</span>
                    <span>Monto</span>
                    <span className="text-center">Pagada</span>
                    <span />
                  </div>
                  {editForm.parcialidades.map((p, i) => (
                    <div
                      key={i}
                      className="grid grid-cols-[1fr_1fr_4.5rem_1.5rem] items-center gap-2"
                    >
                      <input
                        type="date"
                        className={inputClasses}
                        value={p.fecha}
                        onChange={(e) =>
                          setParcialidad(i, { fecha: e.target.value })
                        }
                      />
                      <div className="relative">
                        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-zinc-400">
                          $
                        </span>
                        <input
                          type="text"
                          inputMode="decimal"
                          className={`${inputClasses} pl-7`}
                          value={fmtMXNInput(p.monto)}
                          onChange={(e) =>
                            setParcialidad(i, {
                              monto: normalizeMoneyInput(e.target.value),
                            })
                          }
                        />
                      </div>
                      <div className="flex justify-center">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300 text-[#1b4d2e] focus:ring-[#1b4d2e]"
                          checked={p.pagado}
                          onChange={(e) =>
                            setParcialidad(i, { pagado: e.target.checked })
                          }
                        />
                      </div>
                      <button
                        type="button"
                        className="text-zinc-400 hover:text-zinc-600"
                        title="Quitar parcialidad"
                        onClick={() =>
                          setEditForm((prev) => ({
                            ...prev,
                            parcialidades: prev.parcialidades.filter(
                              (_, j) => j !== i
                            ),
                          }))
                        }
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-zinc-500">
                  Sin parcialidades. Agrega pagos sueltos o genera un plan
                  mensual.
                </p>
              )}

              <div className="mt-3 flex flex-wrap items-end justify-between gap-3">
                <button
                  type="button"
                  className="inline-flex items-center gap-1 rounded-lg border border-zinc-300 px-2.5 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-100"
                  onClick={() =>
                    setEditForm((prev) => ({
                      ...prev,
                      parcialidades: [
                        ...prev.parcialidades,
                        { fecha: "", monto: "", pagado: false },
                      ],
                    }))
                  }
                >
                  <PlusIcon className="h-3.5 w-3.5" />
                  Agregar parcialidad
                </button>
                <div className="flex flex-wrap items-end gap-2">
                  <div className="w-20">
                    <label className="mb-0.5 block text-[11px] text-gray-500">
                      Nº pagos
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="60"
                      className={inputClasses}
                      value={planForm.num}
                      onChange={(e) =>
                        setPlanForm((prev) => ({
                          ...prev,
                          num: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="w-32">
                    <label className="mb-0.5 block text-[11px] text-gray-500">
                      Monto c/u
                    </label>
                    <div className="relative">
                      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-zinc-400">
                        $
                      </span>
                      <input
                        type="text"
                        inputMode="decimal"
                        className={`${inputClasses} pl-7`}
                        value={fmtMXNInput(planForm.monto)}
                        onChange={(e) =>
                          setPlanForm((prev) => ({
                            ...prev,
                            monto: normalizeMoneyInput(e.target.value),
                          }))
                        }
                      />
                    </div>
                  </div>
                  <div className="w-36">
                    <label className="mb-0.5 block text-[11px] text-gray-500">
                      Primer pago
                    </label>
                    <input
                      type="date"
                      className={inputClasses}
                      value={planForm.inicio}
                      onChange={(e) =>
                        setPlanForm((prev) => ({
                          ...prev,
                          inicio: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <Button outline onClick={generarPlan}>
                    Generar plan mensual
                  </Button>
                </div>
              </div>
            </Section>

            {/* Tags y notas */}
            <Section
              icon={TagIcon}
              title="Tags y notas"
              className="sm:col-span-2"
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <div className="mb-2 flex flex-wrap gap-1.5">
                    {editForm.tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-2 py-0.5 text-xs text-zinc-700"
                      >
                        {tag}
                        <button
                          type="button"
                          className="text-zinc-400 hover:text-zinc-700"
                          onClick={() =>
                            setEditForm((prev) => ({
                              ...prev,
                              tags: prev.tags.filter((t) => t !== tag),
                            }))
                          }
                        >
                          <XMarkIcon className="h-3.5 w-3.5" />
                        </button>
                      </span>
                    ))}
                  </div>
                  <input
                    type="text"
                    className={inputClasses}
                    list="admin-billing-tags"
                    placeholder="Escribe un tag y presiona Enter"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === ",") {
                        e.preventDefault();
                        addTag(tagInput);
                      }
                    }}
                    onBlur={() => addTag(tagInput)}
                  />
                  <datalist id="admin-billing-tags">
                    {allTags.map((t) => (
                      <option key={t} value={t} />
                    ))}
                  </datalist>
                </div>
                <div>
                  <DialogTextarea
                    value={editForm.notas}
                    onChange={(e) =>
                      setEditForm((prev) => ({ ...prev, notas: e.target.value }))
                    }
                    rows={4}
                    placeholder="Notas"
                  />
                </div>
              </div>
            </Section>
          </div>
        </DialogBody>
        <DialogActions>
          <Button outline onClick={() => setSelectedVenue(null)}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={updating}>
            {updating ? "Guardando..." : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Groups dialog */}
      <Dialog
        size="2xl"
        open={isGruposOpen}
        onClose={() => {
          setIsGruposOpen(false);
          resetGrupoForm();
        }}
      >
        <DialogTitle>Grupos y descuentos</DialogTitle>
        <div className="mt-1.5 text-sm text-zinc-500">
          Junta los venues de un mismo dueño y aplícales un % de descuento o un
          precio fijo por todo el paquete.
        </div>
        <DialogBody>
          <div className="space-y-4">
            {grupos.length > 0 ? (
              <div className="divide-y divide-zinc-100 overflow-hidden rounded-xl border border-zinc-200">
                {grupos.map((g) => {
                  const count = Object.values(billing).filter(
                    (b) => b?.grupoId === g.id
                  ).length;
                  const total = rows.reduce(
                    (s, r) => s + (r.grupo?.id === g.id ? r.neto : 0),
                    0
                  );
                  return (
                    <div
                      key={g.id}
                      className={`flex items-center justify-between gap-3 px-4 py-3 ${
                        editingGrupoId === g.id ? "bg-zinc-50" : ""
                      }`}
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="truncate text-sm font-medium text-gray-900">
                            {g.nombre}
                          </span>
                          <span className="inline-flex shrink-0 items-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
                            {g.tipo === "fijo"
                              ? `${formatMXN(g.montoFijo)} paquete`
                              : `-${g.descuento || 0}%`}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500">
                          {count} venue(s) · Total {formatMXN(total)}
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        <button
                          type="button"
                          className="rounded-lg p-1.5 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
                          title="Editar grupo"
                          onClick={() => {
                            setEditingGrupoId(g.id);
                            setGrupoForm({
                              nombre: g.nombre,
                              tipo: g.tipo === "fijo" ? "fijo" : "descuento",
                              descuento:
                                g.descuento != null ? String(g.descuento) : "",
                              montoFijo:
                                g.montoFijo != null ? String(g.montoFijo) : "",
                            });
                          }}
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          className="rounded-lg p-1.5 text-zinc-400 hover:bg-red-50 hover:text-red-600"
                          title="Eliminar grupo"
                          onClick={() => handleDeleteGrupo(g)}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-zinc-500">
                Aún no hay grupos. Crea uno para juntar los venues de un mismo
                dueño y aplicarles descuento o precio de paquete.
              </p>
            )}

            <Section
              icon={UserGroupIcon}
              title={editingGrupoId ? "Editar grupo" : "Nuevo grupo"}
              action={
                editingGrupoId ? (
                  <button
                    type="button"
                    className="text-xs font-medium text-zinc-500 underline hover:text-zinc-800"
                    onClick={resetGrupoForm}
                  >
                    Cancelar edición
                  </button>
                ) : null
              }
            >
              <div className="space-y-3">
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-500">
                    Nombre del dueño / grupo
                  </label>
                  <input
                    type="text"
                    className={inputClasses}
                    value={grupoForm.nombre}
                    onChange={(e) =>
                      setGrupoForm((prev) => ({
                        ...prev,
                        nombre: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-500">
                      Tipo de precio
                    </label>
                    <select
                      className={inputClasses}
                      value={grupoForm.tipo}
                      onChange={(e) =>
                        setGrupoForm((prev) => ({
                          ...prev,
                          tipo: e.target.value,
                        }))
                      }
                    >
                      <option value="descuento">% de descuento</option>
                      <option value="fijo">Precio fijo del grupo</option>
                    </select>
                  </div>
                  {grupoForm.tipo === "fijo" ? (
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-500">
                        Monto total del grupo (MXN)
                      </label>
                      <div className="relative">
                        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-zinc-400">
                          $
                        </span>
                        <input
                          type="text"
                          inputMode="decimal"
                          className={`${inputClasses} pl-7`}
                          value={fmtMXNInput(grupoForm.montoFijo)}
                          onChange={(e) =>
                            setGrupoForm((prev) => ({
                              ...prev,
                              montoFijo: normalizeMoneyInput(e.target.value),
                            }))
                          }
                        />
                      </div>
                    </div>
                  ) : (
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-500">
                        % de descuento
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          className={`${inputClasses} pr-8`}
                          value={grupoForm.descuento}
                          onChange={(e) =>
                            setGrupoForm((prev) => ({
                              ...prev,
                              descuento: e.target.value,
                            }))
                          }
                        />
                        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-zinc-400">
                          %
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex justify-end">
                  <Button onClick={handleSaveGrupo} disabled={savingGrupo}>
                    {savingGrupo
                      ? "Guardando..."
                      : editingGrupoId
                      ? "Actualizar grupo"
                      : "Crear grupo"}
                  </Button>
                </div>
              </div>
            </Section>
          </div>
        </DialogBody>
        <DialogActions>
          <Button
            outline
            onClick={() => {
              setIsGruposOpen(false);
              resetGrupoForm();
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AdminVenuePagos;
